type Tense = 'past' | 'future';

type UnitStrings = {
  single: string;
  multiple: (n: number) => string;
};

type TenseStrings = {
  second: UnitStrings;
  minute: UnitStrings;
  hour: UnitStrings;
  day: UnitStrings;
  month: UnitStrings;
  year: UnitStrings;
};

type HumanizeLocale = {
  now: string;
  past: TenseStrings;
  future: TenseStrings;
};

const isPaucal = (n: number): boolean => n >= 2 && n <= 4;

const LOCALES: Record<string, HumanizeLocale> = {
  en: {
    now: 'now',
    past: {
      second: { single: 'one second ago', multiple: (n) => `${n} seconds ago` },
      minute: { single: 'a minute ago', multiple: (n) => `${n} minutes ago` },
      hour: { single: 'an hour ago', multiple: (n) => `${n} hours ago` },
      day: { single: 'yesterday', multiple: (n) => `${n} days ago` },
      month: { single: 'one month ago', multiple: (n) => `${n} months ago` },
      year: { single: 'one year ago', multiple: (n) => `${n} years ago` },
    },
    future: {
      second: { single: 'one second from now', multiple: (n) => `${n} seconds from now` },
      minute: { single: 'a minute from now', multiple: (n) => `${n} minutes from now` },
      hour: { single: 'an hour from now', multiple: (n) => `${n} hours from now` },
      day: { single: 'tomorrow', multiple: (n) => `${n} days from now` },
      month: { single: 'one month from now', multiple: (n) => `${n} months from now` },
      year: { single: 'one year from now', multiple: (n) => `${n} years from now` },
    },
  },
  cs: {
    now: 'teď',
    past: {
      second: { single: 'před sekundou', multiple: (n) => `před ${n} sekundami` },
      minute: { single: 'před minutou', multiple: (n) => `před ${n} minutami` },
      hour: { single: 'před hodinou', multiple: (n) => `před ${n} hodinami` },
      day: { single: 'včera', multiple: (n) => `před ${n} dny` },
      month: { single: 'před měsícem', multiple: (n) => `před ${n} měsíci` },
      year: { single: 'před rokem', multiple: (n) => `před ${n} lety` },
    },
    future: {
      second: { single: 'za sekundu', multiple: (n) => `za ${n} ${isPaucal(n) ? 'sekundy' : 'sekund'}` },
      minute: { single: 'za minutu', multiple: (n) => `za ${n} ${isPaucal(n) ? 'minuty' : 'minut'}` },
      hour: { single: 'za hodinu', multiple: (n) => `za ${n} ${isPaucal(n) ? 'hodiny' : 'hodin'}` },
      day: { single: 'zítra', multiple: (n) => `za ${n} ${isPaucal(n) ? 'dny' : 'dní'}` },
      month: { single: 'za měsíc', multiple: (n) => `za ${n} ${isPaucal(n) ? 'měsíce' : 'měsíců'}` },
      year: { single: 'za rok', multiple: (n) => `za ${n} ${isPaucal(n) ? 'roky' : 'let'}` },
    },
  },
  sk: {
    now: 'teraz',
    past: {
      second: { single: 'pred sekundou', multiple: (n) => `pred ${n} sekundami` },
      minute: { single: 'pred minútou', multiple: (n) => `pred ${n} minútami` },
      hour: { single: 'pred hodinou', multiple: (n) => `pred ${n} hodinami` },
      day: { single: 'včera', multiple: (n) => `pred ${n} dňami` },
      month: { single: 'pred mesiacom', multiple: (n) => `pred ${n} mesiacmi` },
      year: { single: 'pred rokom', multiple: (n) => `pred ${n} rokmi` },
    },
    future: {
      second: { single: 'o sekundu', multiple: (n) => `o ${n} ${isPaucal(n) ? 'sekundy' : 'sekúnd'}` },
      minute: { single: 'o minútu', multiple: (n) => `o ${n} ${isPaucal(n) ? 'minúty' : 'minút'}` },
      hour: { single: 'o hodinu', multiple: (n) => `o ${n} ${isPaucal(n) ? 'hodiny' : 'hodín'}` },
      day: { single: 'zajtra', multiple: (n) => `o ${n} ${isPaucal(n) ? 'dni' : 'dní'}` },
      month: { single: 'o mesiac', multiple: (n) => `o ${n} ${isPaucal(n) ? 'mesiace' : 'mesiacov'}` },
      year: { single: 'o rok', multiple: (n) => `o ${n} ${isPaucal(n) ? 'roky' : 'rokov'}` },
    },
  },
  de: {
    now: 'jetzt',
    past: {
      second: { single: 'vor einer Sekunde', multiple: (n) => `vor ${n} Sekunden` },
      minute: { single: 'vor einer Minute', multiple: (n) => `vor ${n} Minuten` },
      hour: { single: 'vor einer Stunde', multiple: (n) => `vor ${n} Stunden` },
      day: { single: 'gestern', multiple: (n) => `vor ${n} Tagen` },
      month: { single: 'vor einem Monat', multiple: (n) => `vor ${n} Monaten` },
      year: { single: 'vor einem Jahr', multiple: (n) => `vor ${n} Jahren` },
    },
    future: {
      second: { single: 'in einer Sekunde', multiple: (n) => `in ${n} Sekunden` },
      minute: { single: 'in einer Minute', multiple: (n) => `in ${n} Minuten` },
      hour: { single: 'in einer Stunde', multiple: (n) => `in ${n} Stunden` },
      day: { single: 'morgen', multiple: (n) => `in ${n} Tagen` },
      month: { single: 'in einem Monat', multiple: (n) => `in ${n} Monaten` },
      year: { single: 'in einem Jahr', multiple: (n) => `in ${n} Jahren` },
    },
  },
};

function resolveLocale(locale: string): HumanizeLocale {
  const normalized = locale.toLowerCase();
  const twoLetter = normalized.split('-')[0] ?? normalized;
  return LOCALES[normalized] ?? LOCALES[twoLetter] ?? (LOCALES['en'] as HumanizeLocale);
}

function formatUnit(strings: UnitStrings, count: number): string {
  return count === 1 ? strings.single : strings.multiple(count);
}

const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60 * MS_PER_SECOND;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;

export function humanizeDate(date: Date | string | number, locale: string, now: Date | number = Date.now()): string {
  const dateMs = new Date(date).getTime();
  const nowMs = typeof now === 'number' ? now : now.getTime();

  const tense: Tense = dateMs > nowMs ? 'future' : 'past';
  const diffMs = Math.abs(nowMs - dateMs);

  let days = Math.floor(diffMs / MS_PER_DAY);
  let hours = Math.floor(diffMs / MS_PER_HOUR) % 24;
  let minutes = Math.floor(diffMs / MS_PER_MINUTE) % 60;
  let seconds = Math.floor(diffMs / MS_PER_SECOND) % 60;
  const milliseconds = diffMs % MS_PER_SECOND;

  if (milliseconds >= 999) {
    seconds += 1;
  }
  if (seconds >= 59) {
    minutes += 1;
    seconds = 0;
  }
  if (minutes >= 59) {
    hours += 1;
    minutes = 0;
  }
  if (hours >= 24) {
    days += 1;
    hours = 0;
  }

  let months = 0;
  let years = 0;

  if (days >= 30 && days <= 31) {
    months = 1;
  }
  if (days > 31 && days < 365) {
    months = Math.floor(days / 30);
  }
  if (days >= 365) {
    years = Math.floor(days / 365);
  }

  const strings = resolveLocale(locale);
  const tenseStrings = strings[tense];

  if (years > 0) return formatUnit(tenseStrings.year, years);
  if (months > 0) return formatUnit(tenseStrings.month, months);
  if (days > 0) return formatUnit(tenseStrings.day, days);
  if (hours > 0) return formatUnit(tenseStrings.hour, hours);
  if (minutes > 0) return formatUnit(tenseStrings.minute, minutes);
  if (seconds > 0) return formatUnit(tenseStrings.second, seconds);
  return strings.now;
}
